import type { SourceBookProps, SourceListItem } from './types';
import bhiveIcon from 'Assets/images/bhive-logo-short.svg';
import GoogleAuthLogin from 'Components/GoogleAuthLogin';
import ICloudAddressBook from 'Components/ICloudAddressBook';
import Microsoft from 'Components/Microsoft';
import MicrosoftBusiness from 'Components/MicrosoftBusiness';
import MicrosoftPersonal from 'Components/MicrosoftPersonal';
import { ConfirmationModal } from 'Components/shared/ConfirmationModal';
import { FeatureFlag } from 'Components/shared/FeatureFlag';
import SuccessScreenSource from 'Components/SuccessScreenSource';
import { CONTACTS_PER_PAGE } from 'Constants/contacts';
import { NYLAS_V3 } from 'Constants/featureFlags';
import {
  STORE_CONTACT,
  STORE_CONVERSATION,
  STORE_NOTIFICATION,
  STORE_PARTICIPANT,
  STORE_PERSON,
  STORE_PHONE_CALL,
  STORE_UI,
} from 'Constants/stores';
import withRouter from 'Hocs/WithRouter';
import { inject, observer } from 'mobx-react';
import { SourceModelV2 } from 'Models/SourceModelV2';
import React, { useCallback, useState } from 'react';
import { DropdownItemProps, Loader } from 'semantic-ui-react';
import { Styled } from './index.styles';
import { SourcesAddressBookItem } from './SourcesAddressBookItem';

export const permanentProviders: SourceListItem[] = [
  {
    id: -1,
    name: 'Personal contacts',
    text: 'Your personal contacts stored on b-hive and visible only to you. Source can not be removed.',
    providerImageSmall: bhiveIcon,
    email: '',
    createdAt: new Date().valueOf(),
    sourceData: {
      type: 'Bhive',
      accountId: '',
      syncState: 'synced',
      lastSyncedAt: new Date().valueOf(),
    },
  },
  {
    id: -2,
    name: 'Organization contacts',
    text: 'Your account contacts stored on b-hive and visible to everyone on your account. Source can not be removed.',
    providerImageSmall: bhiveIcon,
    email: '',
    createdAt: new Date().valueOf(),
    sourceData: {
      type: 'Bhive',
      accountId: '',
      syncState: 'synced',
      lastSyncedAt: new Date().valueOf(),
    },
  },
];

export const SourcesAddressBookBase = ({
  person,
  navigate,
  notification,
  testId: parentTestId,
}: SourceBookProps) => {
  const testId = `${parentTestId}-sourcesAddressBook`;
  const [showRemoveNotification, setShowRemoveNotification] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [sourceForRemoval, setSourceForRemoval] = useState(null);

  const removeSource = async () => {
    const resp = await person.removeSource(sourceForRemoval);

    if (resp) {
      setShowRemoveNotification(true);
      setShowRemoveModal(false);

      const timeout = setTimeout(() => {
        setShowRemoveNotification(false);
        clearTimeout(timeout);
      }, 1300);
    }
  };

  const toggleRemoveModal = (source: SourceModelV2) => () => {
    setShowRemoveModal(!showRemoveModal);
    setSourceForRemoval(source);
  };

  const resyncSource = (source: SourceModelV2) => () =>
    void person.resyncSource(source);

  const showContactsBasedOnSource = useCallback(
    (source: SourceModelV2) => () => {
      const text = source.email.charAt(0).toUpperCase() + source.email.slice(1);
      const providerValue =
        source.name === 'Personal contacts'
          ? 'personal'
          : source.name === 'Organization contacts'
          ? 'shared'
          : source.id;
      const pickedSource: DropdownItemProps = {
        key: source.id,
        image: { avatar: true, src: source.providerImage },
        text: text,
        value: providerValue,
      };
      person.setContactFilterValue(pickedSource);
      person.getSearchListContacts(
        CONTACTS_PER_PAGE,
        1,
        person.contactSearchTerm,
        undefined,
        pickedSource?.value?.toString()
      );
      navigate('/addressBook/contacts');
    },
    [navigate]
  );

  const handleProperName = () => {
    return person.changeProviderName(
      person.addedNewSource.sourceProvider || ''
    );
  };

  const data = [
    ...permanentProviders,
    ...(person.allSources as SourceListItem[]),
  ];

  const providerName = handleProperName() || '';
  const [removeFromWhere, removeWhat] = ['sources', 'contacts'];

  return (
    <Styled.SourceList>
      {person.addedNewSource.show ? (
        <SuccessScreenSource
          alreadyAddedMessage={person.addedNewSource.alreadyAddedMessage}
          provider={providerName}
          person={person}
          showContactsBasedOnSource={showContactsBasedOnSource}
        />
      ) : (
        <>
          <Styled.Title>Add new source</Styled.Title>
          <Styled.SourceBoxes>
            <GoogleAuthLogin {...{ person }} testId={testId} />
            <FeatureFlag
              flag={NYLAS_V3}
              fallback={
                <>
                  <MicrosoftBusiness {...{ person }} testId={testId} />

                  <MicrosoftPersonal
                    {...{ person }}
                    notification={notification}
                    testId={testId}
                  />
                </>
              }
            >
              <Microsoft {...{ person }} testId={testId} />
            </FeatureFlag>
            <FeatureFlag
              flag={NYLAS_V3}
              fallback={
                <ICloudAddressBook
                  {...{ person }}
                  notification={notification}
                  testId={testId}
                />
              }
            ></FeatureFlag>
          </Styled.SourceBoxes>

          <Styled.Title>Existing contacts</Styled.Title>

          <Styled.SourceWrapper>
            {data?.length > 0 ? (
              data.map((source) => (
                <SourcesAddressBookItem
                  source={source}
                  onShowClick={showContactsBasedOnSource(source)}
                  onResyncClick={resyncSource(source)}
                  onRemoveClick={toggleRemoveModal(source)}
                  testId={testId}
                  key={source.id}
                />
              ))
            ) : data.length === 0 ? (
              <div>
                Your list is empty. Click on the buttons below to add new Source
                to the list.
              </div>
            ) : (
              <Loader />
            )}
          </Styled.SourceWrapper>

          <Styled.Disclaimer variant="paragraph">
            Your contacts will be synchronized between b-hive and your external
            account. Contacts will be visible only to you. Any changes in the
            b-hive will be propagated to your external source and vice versa.
          </Styled.Disclaimer>

          {showRemoveNotification && (
            <Styled.RemoveNotification>
              Source {sourceForRemoval?.name} succesfully removed.
            </Styled.RemoveNotification>
          )}

          <ConfirmationModal
            show={showRemoveModal}
            onClose={toggleRemoveModal(null)}
            onConfirm={removeSource}
            textConfirm="Yes"
            textCancel="No"
            testid={`${testId}-remove`}
          >
            <div>
              You are about to remove <b>{sourceForRemoval?.name}</b> from your{' '}
              {removeFromWhere}. All related {removeWhat} will be removed. Are
              you sure?
            </div>
          </ConfirmationModal>
        </>
      )}
    </Styled.SourceList>
  );
};

export const SourcesAddressBook = inject(
  STORE_PERSON,
  STORE_CONVERSATION,
  STORE_UI,
  STORE_PARTICIPANT,
  STORE_CONTACT,
  STORE_PHONE_CALL,
  STORE_NOTIFICATION
)(withRouter(observer(SourcesAddressBookBase)));
